import { useCallback } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { getBrowserSubscription } from 'lib/webPush';
import { pushSubscriptionQueryKeys } from 'services/api/pushSubscriptions';

/**
 * This browser's own PushSubscription, read from the service worker registration. Held
 * in React Query beside the API's rows so both sides of "is this device enabled" live in one cache.
 * It only changes through enable/disable (which write the entry via `setSubscription`) or a
 * worker-side rotation the user repairs by enabling again — so it is never refetched on its own.
 */
export function useBrowserPushSubscription({ active = true }: { active?: boolean } = {}) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: pushSubscriptionQueryKeys.browser(),
    queryFn: getBrowserSubscription,
    enabled: active,
    staleTime: Infinity,
    // A PushSubscription is a live browser object, not data to diff.
    structuralSharing: false,
  });

  const setSubscription = useCallback(
    (subscription: PushSubscription | null) =>
      queryClient.setQueryData(pushSubscriptionQueryKeys.browser(), subscription),
    [queryClient]
  );

  return {
    subscription: query.data ?? null,
    isPending: query.isPending,
    isSuccess: query.isSuccess,
    setSubscription,
  };
}
